import { useMemo } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Switch,
  Text,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FiCheck, FiCopy, FiRepeat, FiUser } from "react-icons/fi";
import {
  AdminOrgDto,
  AdminUpdateOrganizationRequest,
  AiEndpoint,
  type AiFeatureEndpointsDto,
  BuyerLegalType,
  useAdminUpdateOrganizationMutation,
} from "../../../autogen/bff-api";
import { aiFeaturesOff } from "./aiFeaturesOff";
import { AiUsageSection } from "./AiUsageSection";
import { useAiFeatureLabels } from "./aiFeatureLabels";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { useCreateEditor, useSaveNow } from "../../../common/editing/save-coordinator";
import { DebugPrint } from "../DebugPrint";
import {
  getOrgAccessValues,
  isLockedForSupplier,
  lacksBuyerAndContractAccess,
  OrgAccessFlagKey,
  OrgAccessGroup,
  useOrgAccessGroups,
} from "./useOrgAccessGroups";
import { CountryName } from "../../../common/countries/CountryName";

interface Props {
  organization: AdminOrgDto;
  aiFeatureEndpoints: AiFeatureEndpointsDto[];
  onClose: () => void;
  onImpersonate: () => void;
  onChangeOwner: () => void;
}

type FormValues = {
  name: string;
  buyerLegalType: BuyerLegalType | "";
  aiEndpoints: Record<AiEndpoint, boolean>;
  aiFairUseNok: string;
} & Record<OrgAccessFlagKey, boolean>;

const aiEndpointValues = (allowed: AiEndpoint[]) =>
  Object.fromEntries(
    Object.values(AiEndpoint).map((endpoint) => [
      endpoint,
      allowed.includes(endpoint),
    ]),
  ) as Record<AiEndpoint, boolean>;

const MAX_FAIR_USE_NOK = 10_000_000;

export const isFairUseWithinCeiling = (value: string): boolean =>
  !value.trim() || Number(value.trim().replace(",", ".")) <= MAX_FAIR_USE_NOK;

export const AdminEditOrganizationDrawer = ({
  organization,
  aiFeatureEndpoints,
  onClose,
  onImpersonate,
  onChangeOwner,
}: Props) => {
  const { t } = useTranslation();
  const fairUseMessage = t(
    "Write an amount in kroner, at most 10 000 000, for example 500 or 499,50.",
  );
  const authState = useLoggedInWithOrgContextState();
  const toast = useToast();
  const apiErrorDisplayer = useApiError();
  const groups = useOrgAccessGroups();
  const { onCopy, hasCopied } = useClipboard(organization.id);
  const [update] = useAdminUpdateOrganizationMutation();
  const editorId = `admin-organization:${organization.id}`;
  useCreateEditor<AdminOrgDto, AdminUpdateOrganizationRequest>(editorId, {
    initialState: organization,
    save: async (fields) => {
      await update({
        organizationId: authState.selectedOrg.id,
        orgId: organization.id,
        adminUpdateOrganizationRequest: fields as AdminUpdateOrganizationRequest,
      }).unwrap();
      return organization;
    },
  });
  const { saveNow, isSaving: isLoading } = useSaveNow<AdminOrgDto, AdminUpdateOrganizationRequest>(editorId);

  const {
    handleSubmit,
    register,
    reset,
    watch,
    control,
    formState: { errors, dirtyFields },
  } = useForm<FormValues>({
    defaultValues: {
      name: organization.name,
      buyerLegalType: organization.buyerLegalType ?? "",
      aiEndpoints: aiEndpointValues(organization.aiEndpoints),
      aiFairUseNok: organization.aiFairUseNok ?? "",
      ...getOrgAccessValues(organization),
    },
  });

  const values = watch();
  const savedAccess = useMemo(
    () => getOrgAccessValues(organization),
    [organization],
  );
  const dirtyCount = Object.keys(dirtyFields).length;

  const submit = async ({
    aiEndpoints,
    aiFairUseNok,
    ...formValues
  }: FormValues) => {
    const allowedEndpoints = Object.values(AiEndpoint).filter(
      (endpoint) => aiEndpoints[endpoint],
    );
    try {
      await saveNow({
        ...formValues,
        buyerLegalType: { value: formValues.buyerLegalType || null },
        ...(dirtyFields.aiEndpoints
          ? { aiEndpoints: { value: allowedEndpoints } }
          : {}),
        ...(dirtyFields.aiFairUseNok
          ? {
              aiFairUseNok: {
                value: aiFairUseNok.trim().replace(",", ".") || null,
              },
            }
          : {}),
      });
      toast({
        title: t("Organization updated!"),
        status: "success",
        position: "top-right",
      });
      onClose();
    } catch (e) {
      apiErrorDisplayer.trigger(e);
    }
  };

  return (
    <Drawer isOpen={true} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottom="1px solid" borderColor="smBorder" pr="12">
          <Flex justify="space-between" align="flex-start" gap="3">
            <Box minW="0">
              <Text noOfLines={1}>{organization.name}</Text>
              <HStack spacing="1" mt="0.5">
                <Text
                  fontSize="xs"
                  fontWeight="normal"
                  color="smMuted"
                  noOfLines={1}
                >
                  {organization.id}
                </Text>
                <IconButton
                  aria-label={t("Copy")}
                  icon={hasCopied ? <FiCheck /> : <FiCopy />}
                  size="xs"
                  variant="ghost"
                  onClick={onCopy}
                />
              </HStack>
              <Text
                fontSize="xs"
                fontWeight="normal"
                color="smMuted"
                noOfLines={1}
              >
                {`${organization.owner.firstName ?? ""} ${organization.owner.lastName ?? ""}`.trim()}{" "}
                · {organization.owner.email} · <CountryName country={organization.country} />
              </Text>
              <Button
                size="xs"
                variant="outline"
                leftIcon={<FiUser />}
                mt="1.5"
                onClick={onChangeOwner}
              >
                {t("Change owner")}
              </Button>
            </Box>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiRepeat />}
              flexShrink={0}
              onClick={onImpersonate}
            >
              {t("Impersonate")}
            </Button>
          </Flex>
        </DrawerHeader>
        <DrawerBody
          as="form"
          id="admin-edit-org-form"
          onSubmit={handleSubmit(submit)}
        >
          <FormControl isInvalid={errors.name !== undefined} pt="2">
            <FormLabel htmlFor="name" fontSize="sm">
              {t("Organization name")}
            </FormLabel>
            <Input
              id="name"
              size="sm"
              rounded="md"
              {...register("name", {
                required: t("Please provide a name for the organization") ?? "",
                minLength: {
                  value: 2,
                  message: t("Must be at least 2 characters long"),
                },
              })}
            />
            <FormErrorMessage>
              {errors.name && errors.name.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl pt="4">
            <FormLabel htmlFor="buyerLegalType" fontSize="sm">
              {t("Buyer legal type")}
            </FormLabel>
            <Select
              id="buyerLegalType"
              size="sm"
              rounded="md"
              {...register("buyerLegalType")}
            >
              <option value="">{t("Not set")}</option>
              {Object.values(BuyerLegalType).map((type) => (
                <option key={type} value={type}>
                  {t(type)}
                </option>
              ))}
            </Select>
          </FormControl>
          {groups.map((group) => (
            <AccessGroup
              key={group.title}
              group={group}
              values={values}
              saved={savedAccess}
              control={control}
            />
          ))}
          <AiEndpointsGroup
            isDefault={organization.aiEndpointsAreDefault}
            control={control}
            aiFeatureEndpoints={aiFeatureEndpoints}
          />
          <FormControl isInvalid={!!errors.aiFairUseNok} pt="4">
            <FormLabel htmlFor="aiFairUseNok" fontSize="sm">
              {t("Fair use per month (NOK of model cost)")}
            </FormLabel>
            <Input
              id="aiFairUseNok"
              size="sm"
              rounded="md"
              inputMode="decimal"
              {...register("aiFairUseNok", {
                pattern: {
                  value: /^\s*\d+([.,]\d{1,2})?\s*$/,
                  message: fairUseMessage,
                },
                validate: (value) =>
                  isFairUseWithinCeiling(value) || fairUseMessage,
              })}
            />
            <FormErrorMessage>{errors.aiFairUseNok?.message}</FormErrorMessage>
            <FormHelperText>
              {t(
                "Usage above this amount is billed at model cost plus 10 %. Leave it empty to compute no overage.",
              )}
            </FormHelperText>
          </FormControl>
          <AiUsageSection
            adminOrganizationId={authState.selectedOrg.id}
            organizationId={organization.id}
          />
          <Accordion allowToggle mt="6" mb="4">
            <AccordionItem
              border="1px dashed"
              borderColor="smBorder"
              rounded="md"
            >
              <AccordionButton fontSize="sm" color="smMuted">
                <Box as="span" flex="1" textAlign="left">
                  {t("Raw JSON (debug)")}
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px="2" pb="2">
                <DebugPrint data={organization} />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </DrawerBody>
        <DrawerFooter
          borderTop="1px solid"
          borderColor="smBorder"
          justifyContent="flex-start"
          gap="4"
        >
          <HStack>
            <Button
              size="sm"
              type="submit"
              form="admin-edit-org-form"
              colorScheme="teal"
              isLoading={isLoading}
              isDisabled={dirtyCount === 0}
            >
              {t("Save")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              isDisabled={dirtyCount === 0}
              onClick={() => reset()}
            >
              {t("Discard")}
            </Button>
          </HStack>
          <Text fontSize="sm" color="smMuted">
            {dirtyCount > 0
              ? `${t("Unsaved changes")}: ${dirtyCount}`
              : t("No unsaved changes")}
          </Text>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const AccessGroup = ({
  group,
  values,
  saved,
  control,
}: {
  group: OrgAccessGroup;
  values: FormValues;
  saved: Record<OrgAccessFlagKey, boolean>;
  control: Control<FormValues>;
}) => {
  const { t } = useTranslation();
  const enabledCount = group.flags.filter((flag) => values[flag.key]).length;
  const customerOnlyFlags = group.flags.filter(
    (flag) => flag.needsBuyerOrContractAccess,
  );
  const withoutBuyerOrContracts =
    customerOnlyFlags.length > 0 && lacksBuyerAndContractAccess(values);
  const switchedOnWithoutEffect =
    withoutBuyerOrContracts &&
    customerOnlyFlags.some((flag) => values[flag.key]);
  return (
    <Box mt="6">
      <Flex justify="space-between" align="baseline" mb="1">
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="wide"
          color="smMuted"
        >
          {group.title}
        </Text>
        <Text fontSize="xs" color="smTextDisabled">
          {enabledCount}/{group.flags.length} {t("enabled")}
        </Text>
      </Flex>
      {group.flags.map((flag) => (
        <Flex
          key={flag.key}
          justify="space-between"
          align="center"
          py="1.5"
          borderBottom="1px solid"
          borderColor="smBorder"
          _last={{ borderBottom: "none" }}
        >
          <Box flex="1">
            <FormLabel
              htmlFor={flag.key}
              fontSize="sm"
              fontWeight="normal"
              m="0"
              cursor="pointer"
            >
              {flag.label}
            </FormLabel>
            {isLockedForSupplier(flag, values, saved) && values[flag.key] && (
              <Text fontSize="xs" color="smError">
                {t("Requires the Buyer or Contracts module")}
              </Text>
            )}
          </Box>
          <Controller
            name={flag.key}
            control={control}
            render={({ field }) => (
              <Switch
                id={flag.key}
                size="sm"
                colorScheme="teal"
                isChecked={field.value}
                isDisabled={
                  isLockedForSupplier(flag, values, saved) && !field.value
                }
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            )}
          />
        </Flex>
      ))}
      {withoutBuyerOrContracts && (
        <Text
          mt="2"
          fontSize="sm"
          color={switchedOnWithoutEffect ? "smWarning" : "smMuted"}
        >
          {switchedOnWithoutEffect
            ? t(
                "Has no effect without the Buyer or Contracts module: document chat and other AI features, the SourceMagnet assistant and Fency contract search.",
              )
            : t(
                "Requires the Buyer or Contracts module: document chat and other AI features, the SourceMagnet assistant and Fency contract search.",
              )}
        </Text>
      )}
    </Box>
  );
};

const AiEndpointsGroup = ({
  isDefault,
  control,
  aiFeatureEndpoints,
}: {
  isDefault: boolean;
  control: Control<FormValues>;
  aiFeatureEndpoints: AiFeatureEndpointsDto[];
}) => {
  const { t } = useTranslation();
  const switches = useWatch({ control, name: "aiEndpoints" });
  const allowed = Object.values(AiEndpoint).filter(
    (endpoint) => switches?.[endpoint],
  );
  const off = aiFeaturesOff(allowed, aiFeatureEndpoints);
  const featureLabels = useAiFeatureLabels();
  const labels: Record<AiEndpoint, string> = {
    [AiEndpoint.BedrockEu]: t("AWS Bedrock (in the EU)"),
    [AiEndpoint.OpenaiGlobal]: t("OpenAI (outside the EU)"),
    [AiEndpoint.FencyGlobal]: t("Fency document search (outside the EU)"),
  };
  return (
    <Box mt="6">
      <Flex justify="space-between" align="baseline" mb="1">
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="wide"
          color="smMuted"
        >
          {t("Where AI may process the organization's data")}
        </Text>
        {isDefault && (
          <Text fontSize="xs" color="smTextDisabled">
            {t("Default: EU only")}
          </Text>
        )}
      </Flex>
      {Object.values(AiEndpoint).map((endpoint) => (
        <Flex
          key={endpoint}
          justify="space-between"
          align="center"
          py="1.5"
          borderBottom="1px solid"
          borderColor="smBorder"
          _last={{ borderBottom: "none" }}
        >
          <FormLabel
            htmlFor={endpoint}
            fontSize="sm"
            fontWeight="normal"
            m="0"
            flex="1"
            cursor="pointer"
          >
            {labels[endpoint]}
          </FormLabel>
          <Controller
            name={`aiEndpoints.${endpoint}`}
            control={control}
            render={({ field }) => (
              <Switch
                id={endpoint}
                size="sm"
                colorScheme="teal"
                isChecked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            )}
          />
        </Flex>
      ))}
      {aiFeatureEndpoints.length > 0 &&
        off.length === aiFeatureEndpoints.length && (
          <Text mt="2" fontSize="sm" color="smError">
            {t(
              "No AI feature can run for this organization with this setting.",
            )}
          </Text>
        )}
      {off.length > 0 && off.length < aiFeatureEndpoints.length && (
        <Text mt="2" fontSize="sm" color="smWarning">
          {t("Off with this setting: {{features}}", {
            features: off.map((feature) => featureLabels[feature]).join(", "),
          })}
        </Text>
      )}
    </Box>
  );
};
